import { useMemo, useState, type JSX } from 'react'
import { MessageSquare, ChevronDown, ChevronUp } from 'lucide-react'

/**
 * Renders a conversation description as chat when it holds one.
 *
 * Conversations raised from a WhatsApp or Facebook thread carry the promoted messages
 * into `description` as `[Speaker] text` lines. Shown as a flat block that reads
 * as a wall of text, so the transcript is parsed back into bubbles. Anything
 * that isn't in that shape falls through to the original plain rendering.
 */

export interface TranscriptLine {
  speaker: string
  text: string
  outbound: boolean
}

export interface ParsedTranscript {
  lines: TranscriptLine[]
  facebookPost?: string
}

const SPEAKER_RE = /^\[([^\]]{1,80})\]\s?(.*)$/
const FACEBOOK_POST_RE = /^Facebook Post:\s*(.*)$/

/** Speaker labels the conversation writer uses for the business side of the thread. */
const AGENT_LABELS = new Set(['agent', 'you', 'business', 'system'])

/**
 * Returns null when the text isn't a transcript, so the caller can fall back.
 * A single line is not enough to be confident — plain descriptions can easily
 * start with a bracketed word.
 *
 * @param customerName who the inbound side is. Outbound lines are now stamped
 *   with the real agent's name rather than a literal "Agent", so knowing the
 *   customer is what makes the two sides separable; without it we fall back to
 *   the generic labels older conversations were written with.
 */
export function parseTranscript(
  raw: string,
  customerName?: string | null,
  opts?: { minLines?: number },
): ParsedTranscript | null {
  const lines = raw.split('\n')
  const out: TranscriptLine[] = []
  const customer = customerName?.trim().toLowerCase()
  let facebookPost: string | undefined

  for (const line of lines) {
    // Check for Facebook post header
    const fbPostMatch = FACEBOOK_POST_RE.exec(line)
    if (fbPostMatch) {
      facebookPost = fbPostMatch[1]?.trim()
      continue
    }

    const match = SPEAKER_RE.exec(line)
    if (match) {
      const speaker = match[1]!.trim()
      const key = speaker.toLowerCase()
      out.push({
        speaker,
        text: match[2] ?? '',
        outbound: customer ? key !== customer : AGENT_LABELS.has(key),
      })
      continue
    }
    // Continuation of the previous speaker's message (multi-line text).
    if (out.length > 0) {
      const last = out[out.length - 1]!
      last.text = last.text ? `${last.text}\n${line}` : line
      continue
    }
    // Leading prose before any speaker line — not a transcript.
    if (line.trim()) return null
  }

  // Two lines is the safe threshold for an unknown description — a single
  // bracketed line is more likely prose like "[URGENT] server down". On a chat
  // channel the description is machine-written in this exact shape, so one
  // line is already conclusive and a one-message conversation still renders as chat.
  if (out.length < (opts?.minLines ?? 2)) return null

  return { lines: out, ...(facebookPost ? { facebookPost } : {}) }
}

interface Props {
  description: string
  /** Drives the outbound bubble colour so it matches the source channel. */
  channelOrigin?: string | null
  /** The customer's display name — separates their turns from the agents'. */
  customerName?: string | null
}

const CHANNEL_ACCENT: Record<string, string> = {
  whatsapp: 'bg-[#D9FDD3] text-slate-800',
  facebook: 'bg-[#1877F2] text-white',
  instagram: 'bg-[#C13584] text-white',
}

const COLLAPSE_AFTER = 6

const TRANSCRIPT_CHANNELS = new Set(['whatsapp', 'facebook', 'instagram'])

export function DescriptionTranscript({ description, channelOrigin, customerName }: Props): JSX.Element {
  const chatChannel = TRANSCRIPT_CHANNELS.has((channelOrigin ?? '').toLowerCase())
  const parsed = useMemo(
    () => parseTranscript(description, customerName, chatChannel ? { minLines: 1 } : undefined),
    [description, customerName, chatChannel],
  )
  const [expanded, setExpanded] = useState(false)

  if (!parsed) {
    // Plain descriptions (the email's opening body, mostly) duplicate the
    // first timeline message, so they start collapsed — the toggle keeps them
    // reachable without spending thread space on a wall of text.
    return (
      <div className="bg-white px-5 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 transition-colors hover:text-slate-600"
        >
          <MessageSquare className="h-3 w-3" />
          Description
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {expanded && (
          <div className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap pb-1 text-sm leading-relaxed text-slate-700">
            {description}
          </div>
        )}
      </div>
    )
  }

  const { lines, facebookPost } = parsed
  const outboundClass = CHANNEL_ACCENT[channelOrigin ?? ''] ?? 'bg-blue-600 text-white'
  const hidden = Math.max(0, lines.length - COLLAPSE_AFTER)
  const visible = expanded ? lines : lines.slice(0, COLLAPSE_AFTER)

  return (
    <div className="bg-[#F5F6F7] px-5 py-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <MessageSquare className="h-3 w-3" />
        Chat transcript
        <span className="font-normal normal-case tracking-normal text-slate-400">
          · {lines.length} messages
        </span>
      </div>

      {/* Show Facebook post context if available */}
      {facebookPost && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            Facebook Post
          </div>
          <p className="text-sm text-slate-700">{facebookPost}</p>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {visible.map((line, i) => (
          <div key={i} className={`flex ${line.outbound ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[75%]">
              <span
                className={`mb-0.5 block text-[10px] font-medium text-slate-400 ${
                  line.outbound ? 'pr-1 text-right' : 'pl-1'
                }`}
              >
                {line.speaker}
              </span>
              <div
                className={`rounded-lg px-3 py-1.5 text-sm shadow-sm ${
                  line.outbound ? outboundClass : 'border border-slate-100 bg-white text-slate-800'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{line.text || '—'}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hidden > 0 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> Show {hidden} more message{hidden === 1 ? '' : 's'}
            </>
          )}
        </button>
      )}
    </div>
  )
}
